import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  Text,
  Alert,
  AlertIcon,
  useToast,
  Select,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../lib/firebase';
import { 
  collection, 
  getDocs,
  query,
  where,
} from 'firebase/firestore';

interface FirestoreUser {
  id: string;
  uuid: string;
  name: string;
  displayName?: string;
  email?: string;
}

interface UserPredictions {
  id: string;
  uuid: string;
  name: string;
  total_predicciones: number;
  phase?: string;
}

const ViewQuinielas: React.FC = () => {
  const [usuariosCompletos, setUsuariosCompletos] = useState<UserPredictions[]>([]);
  const [usuariosFaltantes, setUsuariosFaltantes] = useState<UserPredictions[]>([]);
  const [loading, setLoading] = useState(true);
  const [phases, setPhases] = useState<string[]>([]);
  const [selectedPhase, setSelectedPhase] = useState('');
  const toast = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadPhases();
  }, []);

  useEffect(() => {
    if (selectedPhase) {
      loadPredictions();
    }
  }, [selectedPhase]);

  const loadPhases = async () => {
    try {
      const matchesRef = collection(db, 'matches');
      const matchesSnap = await getDocs(matchesRef);
      const uniquePhases = Array.from(new Set(matchesSnap.docs.map(doc => doc.data().phase)));
      setPhases(uniquePhases);
      if (uniquePhases.length > 0) {
        setSelectedPhase(uniquePhases[0]);
      }
    } catch (error) {
      console.error('Error al cargar fases:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las fases',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const loadPredictions = async () => {
    setLoading(true);
    try {
      // Obtener todos los usuarios
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);
      const users = usersSnap.docs.map(doc => ({
        id: doc.id,
        uuid: doc.data().uuid,
        name: doc.data().name || doc.data().displayName || 'Usuario sin nombre',
        ...doc.data()
      })) as FirestoreUser[];

      // Obtener partidos de la fase seleccionada
      const matchesRef = collection(db, 'matches');
      const matchesQuery = query(matchesRef, where('phase', '==', selectedPhase));
      const matchesSnap = await getDocs(matchesQuery);
      const matchesData = matchesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const totalMatchesInPhase = matchesData.length;

      // Obtener predicciones para la fase seleccionada
      const predictionsRef = collection(db, 'predictions');
      const predictionsQuery = query(
        predictionsRef,
        where('match_id', 'in', matchesData.map(match => match.id))
      );
      const predictionsSnap = await getDocs(predictionsQuery);
      
      // Contar predicciones por usuario para la fase seleccionada
      const predictionCounts = predictionsSnap.docs.reduce((acc, doc) => {
        const prediction = doc.data();
        acc[prediction.user_id] = (acc[prediction.user_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Separar usuarios con predicciones completas e incompletas
      const completos: UserPredictions[] = [];
      const faltantes: UserPredictions[] = [];

      users.forEach(user => {
        const totalPredicciones = predictionCounts[user.uuid] || 0;
        const userData = {
          id: user.id,
          uuid: user.uuid,
          name: user.name,
          total_predicciones: totalPredicciones,
          phase: selectedPhase
        };

        if (totalPredicciones === totalMatchesInPhase) {
          completos.push(userData);
        } else {
          faltantes.push(userData);
        }
      });

      setUsuariosCompletos(completos);
      setUsuariosFaltantes(faltantes);
      setLoading(false);

    } catch (error) {
      console.error('Error al cargar predicciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las predicciones',
        status: 'error',
        duration: 3000,
      });
      setLoading(false);
    }
  };

  if (user?.email !== 'htorres@teiker.mx') {
    return (
      <Box p={4}>
        <Heading size="md">No tienes acceso a esta página</Heading>
      </Box>
    );
  }

  return (
    <Container maxW="container.xl" py={5}>
      <Card mb={4}>
        <CardBody>
          <Heading as="h2" size="lg" textAlign="center" mb={4}>
            Estado de Pronósticos por Jornada
          </Heading>
          <FormControl>
            <FormLabel>Seleccionar Jornada</FormLabel>
            <Select 
              value={selectedPhase} 
              onChange={(e) => setSelectedPhase(e.target.value)}
              placeholder="Selecciona la jornada"
            >
              {phases.map((phase) => (
                <option key={phase} value={phase}>{phase}</option>
              ))}
            </Select>
          </FormControl>
        </CardBody>
      </Card>

      <Box mb={8}>
        <Heading as="h3" size="md" mb={4}>
          Usuarios que ya han capturado pronósticos
        </Heading>
        <Table variant="simple" colorScheme="gray">
          <Thead>
            <Tr>
              <Th>Usuario</Th>
              <Th isNumeric>Total de Predicciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {usuariosCompletos.map(user => (
              <Tr key={user.id}>
                <Td>{user.name}</Td>
                <Td isNumeric>{user.total_predicciones}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Box>
        <Heading as="h3" size="md" mb={4} color="red.500">
          Usuarios que faltan por capturar pronósticos
        </Heading>
        <Table variant="simple" colorScheme="red">
          <Thead>
            <Tr>
              <Th>Usuario</Th>
              <Th isNumeric>Predicciones Capturadas</Th>
            </Tr>
          </Thead>
          <Tbody>
            {usuariosFaltantes.length > 0 ? (
              usuariosFaltantes.map(user => (
                <Tr key={user.id}>
                  <Td>{user.name}</Td>
                  <Td isNumeric>{user.total_predicciones}</Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={2} textAlign="center">
                  <Alert status="success">
                    <AlertIcon />
                    ¡Todos los usuarios han capturado sus pronósticos para esta jornada!
                  </Alert>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Container>
  );
};

export default ViewQuinielas; 